import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Loader2, Save, Building2, Palette, Users, Bell, ShieldCheck, CreditCard, Database, Upload, Trash2, Send, AlertTriangle, Check, Plus, Download, KeyRound, Copy as CopyIcon, Monitor, X, Webhook } from "lucide-react";
import AppShell from "../components/app/AppShell";
import PageHeader from "../components/app/PageHeader";
import api, { formatApiError } from "../lib/api";
import { track, EVT } from "../lib/analytics";
import { useAuth } from "../context/AuthContext";
import WebhooksTab from "./WebhooksTab";

const TABS = [
  { id: "profile", label: "Firm profile", icon: Building2 },
  { id: "branding", label: "Branding", icon: Palette },
  { id: "team", label: "Team", icon: Users },
  { id: "notifications", label: "Notifications", icon: Bell },
  { id: "security", label: "Security", icon: ShieldCheck },
  { id: "api", label: "API & developers", icon: KeyRound },
  { id: "integrations", label: "Integrations", icon: Webhook },
  { id: "billing", label: "Billing", icon: CreditCard },
  { id: "data", label: "Data & privacy", icon: Database },
];

function FirmProfileTab({ firm, refresh }) {
  const [form, setForm] = useState(firm);
  const [saving, setSaving] = useState(false);
  useEffect(() => { setForm(firm); }, [firm]);
  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/api/firm/profile", form);
      await refresh();
    } catch (e) { alert(formatApiError(e)); }
    finally { setSaving(false); }
  };
  const f = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div className="card2 p-6 max-w-2xl">
      <div className="grid grid-cols-2 gap-3">
        <div className="col-span-2"><label className="label2">Firm name</label><input className="input2" value={form.name || ""} onChange={f("name")} data-testid="set-firm-name" /></div>
        <div><label className="label2">FCA number</label><input className="input2 t-mono" value={form.fca_number || ""} onChange={f("fca_number")} /></div>
        <div><label className="label2">Website</label><input className="input2" value={form.website || ""} onChange={f("website")} /></div>
        <div className="col-span-2"><label className="label2">Address line 1</label><input className="input2" value={form.address_line1 || ""} onChange={f("address_line1")} /></div>
        <div className="col-span-2"><label className="label2">Address line 2</label><input className="input2" value={form.address_line2 || ""} onChange={f("address_line2")} /></div>
        <div><label className="label2">Town</label><input className="input2" value={form.town || ""} onChange={f("town")} /></div>
        <div><label className="label2">Postcode</label><input className="input2 t-mono" value={form.postcode || ""} onChange={f("postcode")} /></div>
      </div>
      <div className="t-label text-slate2 mt-6 mb-3">Compliance contact</div>
      <div className="grid grid-cols-2 gap-3">
        <div className="col-span-2"><label className="label2">Name</label><input className="input2" value={form.compliance_contact_name || ""} onChange={f("compliance_contact_name")} /></div>
        <div><label className="label2">Email</label><input type="email" className="input2" value={form.compliance_contact_email || ""} onChange={f("compliance_contact_email")} /></div>
        <div><label className="label2">Phone</label><input type="tel" className="input2" value={form.compliance_contact_phone || ""} onChange={f("compliance_contact_phone")} /></div>
      </div>
      <button onClick={save} disabled={saving} className="btn btn-primary mt-6" data-testid="save-profile">
        {saving ? <><Loader2 size={14} className="animate-spin" />Saving</> : <><Save size={14} />Save changes</>}
      </button>
    </div>
  );
}

function BrandingTab({ firm, refresh }) {
  const [primary, setPrimary] = useState(firm.branding?.primary_color || "#4F46E5");
  const [logo, setLogo] = useState(firm.branding?.logo_data_url || "");
  const [saving, setSaving] = useState(false);
  const onFile = (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (f.size > 2 * 1024 * 1024) { alert("Max 2MB"); return; }
    const r = new FileReader(); r.onload = () => setLogo(r.result); r.readAsDataURL(f);
  };
  const save = async () => {
    setSaving(true);
    try { await api.patch("/api/firm/branding", { primary_color: primary, logo_data_url: logo }); await refresh(); }
    catch (e) { alert(formatApiError(e)); }
    finally { setSaving(false); }
  };
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <div className="card2 p-6">
        <label className="label2">Logo (PNG/SVG, max 2MB)</label>
        <label className="block border-2 border-dashed border-border2 rounded-lg p-6 text-center cursor-pointer hover:border-indigo2">
          {logo ? <img src={logo} alt="logo" className="max-h-20 mx-auto" /> : <><Upload size={20} className="mx-auto mb-1 text-slate2" /><span className="t-body-sm">Click to upload</span></>}
          <input type="file" accept="image/*" className="hidden" onChange={onFile} data-testid="branding-logo" />
        </label>
        <label className="label2 mt-5">Primary brand colour</label>
        <div className="flex gap-2 items-center">
          <input type="color" value={primary} onChange={(e) => setPrimary(e.target.value)} className="w-12 h-11 rounded-md border border-border2" data-testid="branding-color" />
          <input type="text" className="input2 t-mono" value={primary} onChange={(e) => setPrimary(e.target.value)} />
        </div>
        <button onClick={save} disabled={saving} className="btn btn-primary mt-6 btn-block" data-testid="save-branding">
          {saving ? <><Loader2 size={14} className="animate-spin" />Saving</> : <><Save size={14} />Save branding</>}
        </button>
      </div>
      <div className="card2 p-6">
        <div className="t-label text-slate2 mb-2">Live preview — client portal</div>
        <div className="border border-border2 rounded-lg overflow-hidden">
          <div className="px-4 py-3 flex items-center gap-3" style={{ background: primary, color: "#fff" }}>
            {logo ? <img src={logo} className="h-8" alt="" /> : <strong>{firm.name}</strong>}
          </div>
          <div className="p-5 bg-surface">
            <div className="t-h4 mb-1">Welcome, [Client]</div>
            <p className="t-body-sm text-slate2 mb-3">Sample preview of how your branded client portal will appear.</p>
            <div className="btn" style={{ background: primary, color: "#fff" }}>Begin onboarding</div>
          </div>
        </div>
      </div>
    </div>
  );
}

function TeamTab() {
  const { user } = useAuth();
  const [data, setData] = useState({ members: [], pending_invitations: [] });
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState("advisor");
  const [sending, setSending] = useState(false);
  const [busy, setBusy] = useState(null);
  const load = async () => { try { const { data } = await api.get("/api/firm/team"); setData(data); } catch {} };
  useEffect(() => { load(); }, []);
  const sendInvite = async () => {
    if (!inviteEmail) return;
    setSending(true);
    try { await api.post("/api/firm/team/invite", { email: inviteEmail, role: inviteRole }); track(EVT.team_member_invited, { role: inviteRole, source: "settings" }); setInviteEmail(""); await load(); }
    catch (e) { alert(formatApiError(e)); }
    finally { setSending(false); }
  };
  const changeRole = async (m, role) => {
    if (role === m.role) return;
    setBusy(m.id);
    try { await api.patch(`/api/firm/team/${m.id}`, { role }); await load(); }
    catch (e) { alert(formatApiError(e)); }
    finally { setBusy(null); }
  };
  const removeMember = async (m) => {
    if (!window.confirm(`Remove ${m.email}? They will be signed out immediately.`)) return;
    setBusy(m.id);
    try { await api.delete(`/api/firm/team/${m.id}`); await load(); }
    catch (e) { alert(formatApiError(e)); }
    finally { setBusy(null); }
  };
  const canManage = user?.role === "firm_admin";
  return (
    <>
      <div className="card2 p-5 mb-5 max-w-2xl">
        <div className="t-label text-slate2 mb-3">Invite a team member</div>
        <div className="flex gap-2">
          <input type="email" placeholder="colleague@firm.co.uk" className="input2 flex-1" value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)} data-testid="team-invite-email" />
          <select className="input2 w-44" value={inviteRole} onChange={(e) => setInviteRole(e.target.value)}>
            <option value="advisor">Advisor</option>
            <option value="compliance_manager">Compliance Manager</option>
            <option value="read_only">Read-only</option>
          </select>
          <button onClick={sendInvite} disabled={sending} className="btn btn-primary" data-testid="team-invite-send">{sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}Send invite</button>
        </div>
      </div>

      <div className="card2 overflow-hidden mb-5">
        <div className="px-5 py-4 border-b border-border2"><h3 className="t-h3">Team members ({data.members.length})</h3></div>
        <table className="w-full">
          <thead className="bg-surface border-b border-border2"><tr className="text-left">
            <th className="px-5 py-3 t-label text-slate2">Member</th>
            <th className="px-5 py-3 t-label text-slate2">Role</th>
            <th className="px-5 py-3 t-label text-slate2">2FA</th>
            <th className="px-5 py-3 t-label text-slate2">Joined</th>
            {canManage && <th className="px-5 py-3 t-label text-slate2 text-right">Actions</th>}
          </tr></thead>
          <tbody className="divide-y divide-border2">
            {data.members.map((m) => (
              <tr key={m.id}>
                <td className="px-5 py-4"><div className="t-body font-medium">{m.first_name} {m.last_name}</div><div className="t-caption">{m.email}</div></td>
                <td className="px-5 py-4">
                  {canManage && m.id !== user?.id ? (
                    <select className="input2 py-1 h-8 text-xs" value={m.role} disabled={busy === m.id} onChange={(e) => changeRole(m, e.target.value)} data-testid={`role-${m.id}`}>
                      <option value="firm_admin">firm admin</option>
                      <option value="compliance_manager">compliance manager</option>
                      <option value="advisor">advisor</option>
                      <option value="read_only">read only</option>
                    </select>
                  ) : (
                    <span className="badge2 badge-locked">{m.role.replace("_", " ")}</span>
                  )}
                </td>
                <td className="px-5 py-4">{m.two_factor_enabled ? <span className="badge2 badge-verified">Enabled</span> : <span className="badge2 badge-pending">Pending</span>}</td>
                <td className="px-5 py-4 t-caption">{m.created_at ? new Date(m.created_at).toLocaleDateString() : "—"}</td>
                {canManage && (
                  <td className="px-5 py-4 text-right">
                    {m.id !== user?.id && (
                      <button onClick={() => removeMember(m)} disabled={busy === m.id} className="btn btn-ghost text-rose2 hover:bg-rose2-50" data-testid={`remove-${m.id}`}>
                        {busy === m.id ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
                        Remove
                      </button>
                    )}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {data.pending_invitations.length > 0 && (
        <div className="card2 overflow-hidden">
          <div className="px-5 py-4 border-b border-border2"><h3 className="t-h3">Pending invitations ({data.pending_invitations.length})</h3></div>
          <table className="w-full">
            <thead className="bg-surface border-b border-border2"><tr className="text-left">
              <th className="px-5 py-3 t-label text-slate2">Email</th>
              <th className="px-5 py-3 t-label text-slate2">Role</th>
              <th className="px-5 py-3 t-label text-slate2">Invited by</th>
              <th className="px-5 py-3 t-label text-slate2">Sent</th>
            </tr></thead>
            <tbody className="divide-y divide-border2">
              {data.pending_invitations.map((inv) => (
                <tr key={inv.id}><td className="px-5 py-3 t-body-sm">{inv.email}</td><td className="px-5 py-3"><span className="badge2 badge-locked">{inv.role.replace("_", " ")}</span></td><td className="px-5 py-3 t-body-sm">{inv.invited_by_name}</td><td className="px-5 py-3 t-caption">{new Date(inv.created_at).toLocaleDateString()}</td></tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

function NotificationsTab() {
  const [prefs, setPrefs] = useState(null);
  const [saved, setSaved] = useState(false);
  useEffect(() => {
    (async () => {
      try { const { data } = await api.get("/api/firm/notification-preferences"); setPrefs(data); }
      catch { setPrefs({}); }
    })();
  }, []);
  const togglePref = async (k, v) => {
    const next = { ...prefs, [k]: v };
    setPrefs(next);
    try {
      await api.patch("/api/firm/notification-preferences", next);
      setSaved(true); setTimeout(() => setSaved(false), 1500);
    } catch (e) { alert(formatApiError(e)); }
  };
  if (!prefs) return <div className="card2 p-6 max-w-2xl flex items-center gap-2 t-body-sm text-slate2"><Loader2 size={14} className="animate-spin" />Loading preferences…</div>;
  return (
    <div className="card2 p-6 max-w-2xl">
      <div className="flex items-center justify-between mb-4">
        <div className="t-label text-slate2">Email me when…</div>
        {saved && <span className="t-caption text-emerald2-dark flex items-center gap-1" data-testid="prefs-saved"><Check size={12} />Saved</span>}
      </div>
      <div className="space-y-3">
        {Object.entries({
          client_opened: "A client opens their invitation link",
          client_started: "A client starts onboarding",
          client_completed: "A client completes onboarding",
          client_stalled_48h: "A client hasn't started after 48 hours",
          client_stalled_7d: "A client hasn't started after 7 days",
          compliance_fail: "A compliance check fails",
          compliance_review: "A compliance check needs review",
          team_joined: "A team member joins",
          billing_failed: "Billing payment fails",
          trial_expiring: "Trial is expiring (7 days)",
          weekly_summary: "Weekly compliance health summary (Mondays)",
        }).map(([k, label]) => (
          <label key={k} className="flex items-center justify-between p-3 border border-border2 rounded-md cursor-pointer hover:bg-surface">
            <span className="t-body-sm">{label}</span>
            <input type="checkbox" checked={!!prefs[k]} onChange={(e) => togglePref(k, e.target.checked)} className="w-4 h-4 accent-indigo2" data-testid={`pref-${k}`} />
          </label>
        ))}
      </div>
    </div>
  );
}

function SecurityTab() {
  const { user, refresh } = useAuth();
  const [stage, setStage] = useState("idle"); // idle | password | qr | enabled | disable
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [qr, setQr] = useState(null);
  const [secret, setSecret] = useState("");
  const [backupCodes, setBackupCodes] = useState([]);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const startSetup = async () => {
    setErr(""); setBusy(true);
    try {
      const { data } = await api.post("/api/2fa/setup", { password });
      setQr(data.qr_code_data_url); setSecret(data.secret); setStage("qr");
    } catch (e) { setErr(formatApiError(e)); }
    finally { setBusy(false); }
  };
  const confirmEnable = async () => {
    setErr(""); setBusy(true);
    try {
      const { data } = await api.post("/api/2fa/enable", { code });
      setBackupCodes(data.backup_codes); setStage("enabled"); track(EVT.two_factor_enabled); await refresh();
    } catch (e) { setErr(formatApiError(e)); }
    finally { setBusy(false); }
  };
  const doDisable = async () => {
    setErr(""); setBusy(true);
    try {
      await api.post("/api/2fa/disable", { password, code });
      setStage("idle"); setPassword(""); setCode(""); await refresh();
    } catch (e) { setErr(formatApiError(e)); }
    finally { setBusy(false); }
  };
  const downloadBackup = () => {
    const blob = new Blob([`Compliche backup codes — ${new Date().toISOString().slice(0,10)}\n\n` + backupCodes.join("\n")], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = "compliche-backup-codes.txt"; a.click(); URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-5 max-w-2xl">
      <div className="card2 p-6">
        <h3 className="t-h3 mb-2">Two-factor authentication</h3>
        <div className="flex items-center justify-between p-4 bg-surface rounded-md mb-4">
          <div>
            <div className="t-body-sm font-medium">{user?.two_factor_enabled ? "Enabled (TOTP)" : "Not yet enabled"}</div>
            <div className="t-caption">{user?.two_factor_required ? "Required for your role" : "Recommended"}</div>
          </div>
          {user?.two_factor_enabled
            ? <button className="btn btn-secondary btn-sm" onClick={() => setStage("disable")} data-testid="2fa-disable-trigger">Disable</button>
            : <button className="btn btn-primary btn-sm" onClick={() => setStage("password")} data-testid="2fa-enable-trigger">Enable 2FA</button>}
        </div>
        {stage === "password" && (
          <div className="space-y-3" data-testid="2fa-password-stage">
            <p className="t-body-sm text-slate2">Enter your password to begin 2FA setup.</p>
            <input type="password" className="input2" placeholder="Your password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="2fa-password" />
            {err && <div className="t-caption text-rose2">{err}</div>}
            <div className="flex gap-2">
              <button onClick={startSetup} disabled={busy || !password} className="btn btn-primary" data-testid="2fa-password-submit">{busy ? <Loader2 size={14} className="animate-spin" /> : null}Continue</button>
              <button onClick={() => { setStage("idle"); setPassword(""); setErr(""); }} className="btn btn-secondary">Cancel</button>
            </div>
          </div>
        )}
        {stage === "qr" && (
          <div className="space-y-3" data-testid="2fa-qr-stage">
            <p className="t-body-sm text-slate2">Scan the QR with Google Authenticator, 1Password, Authy, or any TOTP app.</p>
            <div className="flex flex-col sm:flex-row items-center gap-4">
              {qr && <img src={qr} alt="2FA QR code" className="w-40 h-40 bg-white p-2 rounded-md" />}
              <div className="t-caption">
                <div>Or paste this secret manually:</div>
                <div className="t-mono mt-1 break-all">{secret}</div>
              </div>
            </div>
            <input className="input2 t-mono" placeholder="6-digit code from your app" maxLength={6} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} data-testid="2fa-code" />
            {err && <div className="t-caption text-rose2">{err}</div>}
            <div className="flex gap-2">
              <button onClick={confirmEnable} disabled={busy || code.length !== 6} className="btn btn-primary" data-testid="2fa-confirm">{busy ? <Loader2 size={14} className="animate-spin" /> : null}Verify &amp; enable</button>
              <button onClick={() => { setStage("idle"); setCode(""); setErr(""); }} className="btn btn-secondary">Cancel</button>
            </div>
          </div>
        )}
        {stage === "enabled" && (
          <div className="space-y-3" data-testid="2fa-enabled-stage">
            <div className="p-4 bg-emerald2-50 rounded-md text-emerald2-dark t-body-sm flex items-start gap-2"><Check size={16} className="mt-0.5" />2FA enabled. Save your backup codes below — they're your only recovery method if you lose your phone.</div>
            <div className="grid grid-cols-2 gap-2 t-mono p-3 border border-border2 rounded-md">
              {backupCodes.map((c) => <div key={c} className="text-center py-1">{c}</div>)}
            </div>
            <button onClick={downloadBackup} className="btn btn-secondary"><Download size={14} />Download backup codes</button>
          </div>
        )}
        {stage === "disable" && (
          <div className="space-y-3" data-testid="2fa-disable-stage">
            <p className="t-body-sm text-rose2">Disabling 2FA reduces your account security. Confirm with your password + a current TOTP code.</p>
            <input type="password" className="input2" placeholder="Your password" value={password} onChange={(e) => setPassword(e.target.value)} />
            <input className="input2 t-mono" placeholder="6-digit code" maxLength={6} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} />
            {err && <div className="t-caption text-rose2">{err}</div>}
            <div className="flex gap-2">
              <button onClick={doDisable} disabled={busy || !password || code.length !== 6} className="btn btn-danger" data-testid="2fa-disable-confirm">{busy ? <Loader2 size={14} className="animate-spin" /> : null}Disable 2FA</button>
              <button onClick={() => { setStage("idle"); setPassword(""); setCode(""); setErr(""); }} className="btn btn-secondary">Cancel</button>
            </div>
          </div>
        )}
        <p className="t-body-sm text-slate2 mt-3">Backup codes are generated when you enable 2FA. Store them somewhere safe — they're your recovery method if you lose your authenticator.</p>
      </div>
      <SessionsPanel />
      <div className="card2 p-6">
        <h3 className="t-h3 mb-2">Login history</h3>
        <p className="t-body-sm text-slate2">View your last 50 logins (successful and failed) in your <a className="text-indigo2 hover:underline" href="/app/audit-trail">audit trail</a>.</p>
      </div>
    </div>
  );
}

function BillingTab() {
  const [sub, setSub] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [plansData, setPlansData] = useState(null);
  const [busy, setBusy] = useState(null);
  const [period, setPeriod] = useState("monthly");
  const [confirmCancel, setConfirmCancel] = useState(false);

  const load = async () => {
    try {
      const [s, p, i] = await Promise.all([
        api.get("/api/billing/subscription"),
        api.get("/api/billing/plans"),
        api.get("/api/billing/invoices").catch(() => ({ data: { invoices: [] } })),
      ]);
      setSub(s.data); setPlansData(p.data); setInvoices(i.data.invoices || []);
      setPeriod(s.data?.billing_period || "monthly");
    } catch (e) { /* ignore */ }
  };
  useEffect(() => { load(); }, []);

  const goCheckout = async (planKey, billing_period) => {
    setBusy(planKey + "_" + billing_period);
    try {
      track(EVT.plan_upgrade_started, { plan: planKey, billing_period, source: "settings" });
      const { data: r } = await api.post("/api/billing/checkout-session", {
        plan: planKey, billing_period, origin_url: window.location.origin,
      });
      if (r.url) window.location.href = r.url;
      else alert(r.message || "Could not start checkout");
    } catch (e) { alert(formatApiError(e)); }
    finally { setBusy(null); }
  };

  const openPortal = async () => {
    setBusy("portal");
    try {
      const { data: r } = await api.post("/api/billing/portal-session", { origin_url: window.location.origin });
      if (r.url) window.location.href = r.url;
    } catch (e) { alert(formatApiError(e)); }
    finally { setBusy(null); }
  };

  const cancel = async () => {
    setBusy("cancel");
    try {
      await api.post("/api/billing/cancel");
      track(EVT.trial_cancelled, { plan: sub?.plan?.key });
      setConfirmCancel(false); await load();
    } catch (e) { alert(formatApiError(e)); }
    finally { setBusy(null); }
  };
  const uncancel = async () => {
    setBusy("uncancel");
    try { await api.post("/api/billing/uncancel"); await load(); }
    catch (e) { alert(formatApiError(e)); }
    finally { setBusy(null); }
  };

  if (!sub) return <div className="card2 p-6 t-body-sm text-slate2">Loading billing…</div>;
  const plan = sub.plan;
  const usage = sub.usage || {};
  const isTrial = plan?.key === "trial" || sub.subscription_status === "trialing";
  const overage = sub.overage || {};

  return (
    <div className="space-y-6 max-w-3xl">
      {/* Current plan card */}
      <div className="card2 p-6" data-testid="billing-current-plan">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="t-label text-slate2">Current plan</div>
            <h3 className="t-h2 mt-1">{plan?.label || "Trial"}</h3>
            <p className="t-body-sm text-slate2 mt-1">
              {isTrial
                ? `Trial — ${sub.trial_ends_at ? `ends ${new Date(sub.trial_ends_at).toLocaleDateString()}` : "14-day evaluation"}`
                : `£${sub.billing_period === "annual" ? plan?.price_annual_gbp + "/year" : plan?.price_monthly_gbp + "/month"}`}
            </p>
            {sub.cancel_at_period_end && (
              <p className="t-body-sm text-amber-600 mt-2">⚠ Subscription scheduled to cancel at period end. <button onClick={uncancel} disabled={busy==="uncancel"} className="text-indigo2 hover:underline">Reactivate</button></p>
            )}
            {sub.subscription_status === "past_due" && (
              <p className="t-body-sm text-rose-600 mt-2">⚠ Payment failed. Please update your card via the Customer Portal.</p>
            )}
          </div>
          <span className={`badge2 ${isTrial ? "badge-pending" : sub.subscription_status === "active" ? "badge-verified" : "badge-pending"}`}>
            {isTrial ? "Trial" : sub.subscription_status}
          </span>
        </div>

        {sub.stripe_customer_id && (
          <div className="mt-4 flex gap-2 flex-wrap">
            <button onClick={openPortal} disabled={busy==="portal"} className="btn btn-secondary" data-testid="billing-open-portal">
              {busy==="portal" ? <Loader2 size={14} className="animate-spin" /> : null}Manage in Stripe Portal
            </button>
            {!sub.cancel_at_period_end && (
              <button onClick={() => setConfirmCancel(true)} className="btn btn-ghost text-rose-600" data-testid="billing-cancel">Cancel subscription</button>
            )}
          </div>
        )}
      </div>

      {/* Plan picker — show only if Trial or to allow upgrades/downgrades */}
      {plansData?.plans && (
        <div className="card2 p-6">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <h3 className="t-h3">{isTrial ? "Choose a plan" : "Change plan"}</h3>
            <div className="inline-flex rounded-full border border-border2 p-1 bg-bg2 text-sm">
              <button onClick={() => setPeriod("monthly")} className={`px-3 py-1 rounded-full ${period==="monthly"?"bg-white shadow-sm font-medium":""}`} data-testid="billing-period-monthly">Monthly</button>
              <button onClick={() => setPeriod("annual")} className={`px-3 py-1 rounded-full ${period==="annual"?"bg-white shadow-sm font-medium":""}`} data-testid="billing-period-annual">Annual (save ~17%)</button>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {plansData.plans.map((p) => {
              const isCurrent = !isTrial && plan?.key === p.key && sub.billing_period === period;
              const priceGbp = period === "annual" ? p.annual_gbp : p.monthly_gbp;
              const display = period === "annual" ? `£${(priceGbp/12).toFixed(0)}/mo` : `£${priceGbp}/mo`;
              return (
                <div key={p.key} className={`rounded-xl border ${isCurrent ? "border-indigo2 bg-indigo2/5" : "border-border2"} p-4`} data-testid={`billing-plan-${p.key}`}>
                  <div className="t-label text-slate2">{p.label.replace("Compliche ", "")}</div>
                  <div className="mt-1 flex items-baseline gap-2">
                    <span className="text-2xl font-bold">{display}</span>
                    {period === "annual" && <span className="t-caption text-slate2">£{priceGbp}/yr</span>}
                  </div>
                  <ul className="t-body-sm text-slate2 mt-3 space-y-1">
                    <li>{p.clients_limit === null ? "Unlimited" : p.clients_limit} clients</li>
                    <li>{p.scans_per_month === null ? "Unlimited" : p.scans_per_month + "/mo"} scans</li>
                    <li>{p.team_limit === null ? "Unlimited" : p.team_limit} team {p.team_limit === 1 ? "seat" : "seats"}</li>
                    {p.features?.identity_verification && <li>Identity verification (Didit)</li>}
                    {p.features?.api_access && <li>API access</li>}
                    {p.features?.audit_export && <li>Audit export</li>}
                    {p.minimum_commitment_months && <li className="text-amber-600">Min {p.minimum_commitment_months}-month commitment</li>}
                  </ul>
                  <button
                    onClick={() => goCheckout(p.key, period)}
                    disabled={isCurrent || busy === `${p.key}_${period}`}
                    className={`w-full mt-4 btn ${p.key === "practitioner" ? "btn-primary" : "btn-secondary"} ${isCurrent ? "opacity-50 cursor-not-allowed" : ""}`}
                    data-testid={`billing-select-${p.key}`}>
                    {busy === `${p.key}_${period}` ? <Loader2 size={14} className="animate-spin" /> : null}
                    {isCurrent ? "Current plan" : (isTrial ? "Start with " + p.label.replace("Compliche ", "") : "Switch to this")}
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Usage */}
      <div className="card2 p-6">
        <h3 className="t-h3 mb-3">Usage this period</h3>
        {[
          { label: "Active client onboardings", value: usage.clients_active || 0, max: plan?.clients_limit, key: "clients" },
          { label: "Compliance scans this month", value: usage.scans_this_month || 0, max: plan?.scans_per_month, key: "scans" },
          { label: "Team members", value: usage.team || 0, max: plan?.team_limit, key: "team" },
          { label: "Identity verifications (Didit)", value: usage.didit_this_month || 0, max: plan?.didit_verifications_per_month, key: "idv" },
          { label: "AML screenings (Dilisense)", value: usage.aml_this_month || 0, max: plan?.aml_screenings_per_month, key: "aml" },
          { label: "API calls this month", value: usage.api_calls_this_month || 0, max: plan?.api_calls_per_month, key: "api" },
        ].map((u) => {
          const pct = u.max == null ? 0 : Math.min(100, (u.value / Math.max(u.max, 1)) * 100);
          return (
            <div key={u.label} className="mb-3" data-testid={`usage-${u.key}`}>
              <div className="flex justify-between t-body-sm mb-1"><span>{u.label}</span><span className="t-mono">{u.value} / {u.max == null ? "Unlimited" : u.max}</span></div>
              <div className="h-1.5 bg-border2 rounded-full"><div className={`h-full rounded-full transition-all ${pct >= 90 ? "bg-rose-500" : pct >= 70 ? "bg-amber-500" : "bg-indigo2"}`} style={{ width: `${pct}%` }} /></div>
            </div>
          );
        })}
      </div>

      {/* Overage */}
      {plan?.key === "professional" && (overage.identity_units > 0 || overage.aml_units > 0) && (
        <div className="card2 p-6 border-amber-300 bg-amber-50">
          <h3 className="t-h3 mb-2">Overage this period</h3>
          <p className="t-body-sm text-slate2 mb-3">Used beyond your plan allowance — charged on your next invoice.</p>
          <div className="space-y-1 t-body-sm">
            {overage.identity_units > 0 && <div className="flex justify-between"><span>{overage.identity_units} extra identity verifications</span><span className="t-mono">£{overage.identity_cost_gbp?.toFixed(2)}</span></div>}
            {overage.aml_units > 0 && <div className="flex justify-between"><span>{overage.aml_units} extra AML screenings</span><span className="t-mono">£{overage.aml_cost_gbp?.toFixed(2)}</span></div>}
          </div>
        </div>
      )}

      {/* Invoices */}
      {invoices.length > 0 && (
        <div className="card2 p-6">
          <h3 className="t-h3 mb-3">Billing history</h3>
          <table className="w-full t-body-sm">
            <thead className="text-slate2 text-left">
              <tr><th className="py-2">Invoice</th><th>Date</th><th>Status</th><th className="text-right">Amount</th><th></th></tr>
            </thead>
            <tbody>
              {invoices.map((inv) => (
                <tr key={inv.id} className="border-t border-border2">
                  <td className="py-2 t-mono">{inv.number || inv.id.slice(0,12)}</td>
                  <td>{inv.created ? new Date(inv.created * 1000).toLocaleDateString() : "—"}</td>
                  <td><span className={`badge2 ${inv.status === "paid" ? "badge-verified" : "badge-pending"}`}>{inv.status}</span></td>
                  <td className="text-right t-mono">£{(inv.amount_paid || inv.amount_due || 0).toFixed(2)}</td>
                  <td className="text-right">{inv.invoice_pdf && <a href={inv.invoice_pdf} target="_blank" rel="noreferrer" className="text-indigo2 hover:underline">PDF</a>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Cancel confirm modal */}
      {confirmCancel && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={() => setConfirmCancel(false)}>
          <div className="card2 max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
            <h3 className="t-h3 mb-2">Cancel subscription?</h3>
            <p className="t-body-sm text-slate2 mb-4">Your plan stays active until the end of the current billing period. After that you'll be downgraded to Starter and will lose API access, audit export and identity verification.</p>
            <p className="t-body-sm text-slate2 mb-4">Prefer to downgrade to Starter instead (£49/mo)? You'd keep core compliance functionality.</p>
            <div className="flex justify-end gap-2">
              <button onClick={() => setConfirmCancel(false)} className="btn btn-ghost">Keep my plan</button>
              <button onClick={() => { setConfirmCancel(false); goCheckout("starter", period); }} className="btn btn-secondary">Downgrade to Starter</button>
              <button onClick={cancel} disabled={busy==="cancel"} className="btn btn-danger" data-testid="billing-cancel-confirm">{busy==="cancel" ? <Loader2 size={14} className="animate-spin" /> : "Cancel anyway"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function DataTab({ firm, refresh }) {
  const [busy, setBusy] = useState(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [confirmText, setConfirmText] = useState("");

  const downloadFile = async (url, filename) => {
    const base = process.env.REACT_APP_BACKEND_URL;
    const isPost = url.startsWith("/api/firm/data-export");
    const headers = {};
    if (isPost) {
      // Echo the CSRF cookie back per double-submit protection
      const csrf = (document.cookie.match(/(?:^|; )compliche_csrf=([^;]*)/) || [])[1];
      if (csrf) headers["X-CSRF-Token"] = decodeURIComponent(csrf);
    }
    const res = await fetch(`${base}${url}`, { credentials: "include", method: isPost ? "POST" : "GET", headers });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const blob = await res.blob();
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = filename; a.click(); URL.revokeObjectURL(a.href);
  };

  const exportData = async () => {
    setBusy("export");
    try { await downloadFile("/api/firm/data-export", `compliche-export-${Date.now()}.zip`); }
    catch (e) { alert("Export failed: " + e.message); }
    finally { setBusy(null); }
  };
  const downloadDPA = async () => {
    setBusy("dpa");
    try { await downloadFile("/api/firm/dpa-template", "compliche-dpa-template.pdf"); }
    catch (e) { alert("Download failed: " + e.message); }
    finally { setBusy(null); }
  };
  const confirmDelete = async () => {
    setBusy("delete");
    try {
      await api.post("/api/firm/delete-request", { firm_name_confirmation: confirmText });
      setDeleteOpen(false); setConfirmText(""); await refresh();
      alert("Deletion scheduled. You have 30 days to undo.");
    } catch (e) { alert(formatApiError(e)); }
    finally { setBusy(null); }
  };
  const cancelDeletion = async () => {
    setBusy("cancel");
    try { await api.post("/api/firm/delete-cancel"); await refresh(); }
    catch (e) { alert(formatApiError(e)); }
    finally { setBusy(null); }
  };

  return (
    <div className="space-y-5 max-w-2xl">
      {firm?.deletion_scheduled_at && (
        <div className="card2 p-5 border-l-4 border-rose2 bg-rose2-50/30" data-testid="deletion-banner">
          <div className="flex items-start gap-3">
            <AlertTriangle size={18} className="text-rose2 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <div className="t-body-sm font-medium">Account deletion scheduled for {new Date(firm.deletion_scheduled_at).toLocaleDateString()}</div>
              <p className="t-caption mt-1">All firm data will be permanently deleted on that date (audit trail records are retained 7 years per FCA). You can cancel any time before then.</p>
            </div>
            <button onClick={cancelDeletion} disabled={busy === "cancel"} className="btn btn-secondary btn-sm" data-testid="cancel-deletion">
              {busy === "cancel" ? <Loader2 size={14} className="animate-spin" /> : null}Cancel deletion
            </button>
          </div>
        </div>
      )}
      <div className="card2 p-6">
        <h3 className="t-h3 mb-2">Data export (GDPR Article 20)</h3>
        <p className="t-body-sm text-slate2 mb-4">Export your firm's entire dataset — clients, flows, audit trail, compliance checks, team members, firm profile — as a downloadable ZIP archive of CSV and JSON files. We'll also email you a confirmation.</p>
        <button onClick={exportData} disabled={busy === "export"} className="btn btn-secondary" data-testid="export-data">
          {busy === "export" ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
          {busy === "export" ? "Generating…" : "Request export"}
        </button>
      </div>
      <div className="card2 p-6">
        <h3 className="t-h3 mb-2">Data retention</h3>
        <p className="t-body-sm text-slate2 mb-3">FCA standard requires 7-year retention of client data and audit trail. You can choose to extend this for specific records.</p>
        <select className="input2 max-w-sm">
          <option>Keep for 7 years (recommended, FCA standard)</option>
          <option>Keep for 5 years</option>
          <option>Keep indefinitely</option>
        </select>
      </div>
      <div className="card2 p-6">
        <h3 className="t-h3 mb-2">Data Processing Agreement</h3>
        <p className="t-body-sm text-slate2 mb-3">Generates a pre-filled DPA PDF using your firm's name, address and contact details. Suitable as a starting point for any UK-based data processor relationship — please have your own counsel review before signing.</p>
        <button onClick={downloadDPA} disabled={busy === "dpa"} className="btn btn-secondary" data-testid="download-dpa">
          {busy === "dpa" ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
          {busy === "dpa" ? "Generating…" : "Download DPA template"}
        </button>
      </div>
      <div className="card2 p-6 border-l-3" style={{ borderLeftColor: "#EF4444", borderLeftWidth: 3 }}>
        <h3 className="t-h3 mb-2 text-rose2 flex items-center gap-2"><AlertTriangle size={18} />Delete account</h3>
        <p className="t-body-sm text-slate2 mb-3">Permanently delete your firm account. Audit trail records will be retained for 7 years as required by FCA. There's a 30-day grace period during which you can undo.</p>
        <button onClick={() => setDeleteOpen(true)} disabled={!!firm?.deletion_scheduled_at} className="btn btn-danger" data-testid="delete-account">Delete this account</button>
      </div>

      {deleteOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4" onClick={() => setDeleteOpen(false)}>
          <div className="card2 p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()} data-testid="delete-modal">
            <h3 className="t-h3 mb-2 flex items-center gap-2 text-rose2"><AlertTriangle size={20} />Are you absolutely sure?</h3>
            <p className="t-body-sm text-slate2 mb-3">This action schedules permanent deletion of your firm account in 30 days. All clients, flows and compliance scans will be erased. Audit trail records are retained 7 years per FCA.</p>
            <p className="t-body-sm mb-2">Type your firm name <span className="t-mono font-semibold">{firm?.name}</span> to confirm:</p>
            <input className="input2 mb-3" value={confirmText} onChange={(e) => setConfirmText(e.target.value)} data-testid="delete-confirm-input" />
            <div className="flex gap-2 justify-end">
              <button onClick={() => setDeleteOpen(false)} className="btn btn-secondary">Cancel</button>
              <button onClick={confirmDelete} disabled={busy === "delete" || confirmText.trim() !== (firm?.name || "").trim()} className="btn btn-danger" data-testid="delete-confirm">
                {busy === "delete" ? <Loader2 size={14} className="animate-spin" /> : null}Schedule deletion
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ApiTab() {
  const [data, setData] = useState({ keys: [], plan: null, monthly_calls: 0, monthly_limit: null });
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [justCreated, setJustCreated] = useState("");
  const [copied, setCopied] = useState(false);

  const load = async () => {
    try { const { data } = await api.get("/api/keys"); setData(data); } catch (e) { /* may be 403 on starter */ }
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!name.trim()) return;
    setBusy(true);
    try {
      const { data: res } = await api.post("/api/keys", { name });
      track(EVT.api_key_created);
      setJustCreated(res.key);
      setName("");
      await load();
    } catch (e) { alert(formatApiError(e)); }
    finally { setBusy(false); }
  };
  const revoke = async (id) => {
    if (!window.confirm("Revoke this API key? Any application using it will stop working immediately.")) return;
    try { await api.delete(`/api/keys/${id}`); await load(); }
    catch (e) { alert(formatApiError(e)); }
  };
  const copy = (s) => { navigator.clipboard?.writeText(s); setCopied(true); setTimeout(() => setCopied(false), 2000); };
  const plan = data.plan;
  const noAccess = plan && !plan.features?.api_access;

  return (
    <div className="space-y-5 max-w-3xl">
      <div className="card2 p-6">
        <h3 className="t-h3 mb-1">Compliche public API</h3>
        <p className="t-body-sm text-slate2 mb-4">Programmable access to compliance scoring, audit verification, firm verification, regulatory checks and client onboarding packs. <a href="/developers" target="_blank" rel="noreferrer" className="text-indigo2 hover:underline">View developer docs →</a></p>

        {noAccess ? (
          <div className="p-4 bg-amber2-50 border border-amber2/30 rounded-md t-body-sm">
            <strong>API access requires Professional or above.</strong> Your current plan is <strong>{plan?.label}</strong>. <a href="/app/settings#billing" className="text-indigo2 hover:underline">Upgrade plan →</a>
          </div>
        ) : (
          <>
            {plan && (
              <div className="grid grid-cols-3 gap-3 mb-5">
                <div className="bg-surface rounded-md p-3"><div className="t-caption">Plan</div><div className="t-h4 mt-1">{plan.label}</div></div>
                <div className="bg-surface rounded-md p-3"><div className="t-caption">Calls this month</div><div className="t-h4 mt-1">{data.monthly_calls.toLocaleString()}</div></div>
                <div className="bg-surface rounded-md p-3"><div className="t-caption">Monthly limit</div><div className="t-h4 mt-1">{data.monthly_limit === null ? "Unlimited" : data.monthly_limit.toLocaleString()}</div></div>
              </div>
            )}
            <div className="flex gap-2 mb-4">
              <input className="input2 flex-1" placeholder="Key name (e.g. 'CMS plugin')" value={name} onChange={(e) => setName(e.target.value)} data-testid="apikey-name" />
              <button onClick={create} disabled={busy || !name.trim()} className="btn btn-primary" data-testid="apikey-create">
                {busy ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}Create key
              </button>
            </div>

            {justCreated && (
              <div className="p-4 mb-4 bg-emerald2-50 border border-emerald2-dark/30 rounded-md" data-testid="apikey-created">
                <div className="t-body-sm font-medium mb-1">Save this key now — you won't see it again.</div>
                <div className="flex items-center gap-2">
                  <code className="flex-1 t-mono text-xs p-2 bg-white rounded border border-border2 break-all">{justCreated}</code>
                  <button onClick={() => copy(justCreated)} className="btn btn-secondary btn-sm">{copied ? <Check size={14} /> : <CopyIcon size={14} />}{copied ? "Copied" : "Copy"}</button>
                </div>
              </div>
            )}

            {data.keys.length === 0 ? (
              <p className="t-caption">No API keys yet. Create one above to start calling the Compliche API.</p>
            ) : (
              <table className="w-full">
                <thead className="bg-surface border-b border-border2 text-left">
                  <tr><th className="px-3 py-2 t-label text-slate2">Name</th><th className="px-3 py-2 t-label text-slate2">Prefix</th><th className="px-3 py-2 t-label text-slate2">Created</th><th className="px-3 py-2 t-label text-slate2">Status</th><th className="px-3 py-2"></th></tr>
                </thead>
                <tbody className="divide-y divide-border2">
                  {data.keys.map((k) => (
                    <tr key={k.id} data-testid={`apikey-row-${k.id}`}>
                      <td className="px-3 py-2 t-body-sm font-medium">{k.name}</td>
                      <td className="px-3 py-2 t-mono text-xs">{k.prefix}…</td>
                      <td className="px-3 py-2 t-caption">{new Date(k.created_at).toLocaleDateString()}</td>
                      <td className="px-3 py-2">{k.revoked_at ? <span className="badge2 badge-failed">Revoked</span> : <span className="badge2 badge-verified">Active</span>}</td>
                      <td className="px-3 py-2 text-right">{!k.revoked_at && <button onClick={() => revoke(k.id)} className="btn btn-ghost btn-sm text-rose2"><Trash2 size={14} />Revoke</button>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function SessionsPanel() {
  const [sessions, setSessions] = useState([]);
  const [busy, setBusy] = useState(null);
  const load = async () => { try { const { data } = await api.get("/api/auth/sessions"); setSessions(data.items); } catch {} };
  useEffect(() => { load(); }, []);
  const terminate = async (id) => { setBusy(id); try { await api.delete(`/api/auth/sessions/${id}`); await load(); } catch (e) { alert(formatApiError(e)); } finally { setBusy(null); } };
  const terminateOthers = async () => { setBusy("others"); try { await api.post("/api/auth/sessions/terminate-others"); await load(); } catch (e) { alert(formatApiError(e)); } finally { setBusy(null); } };
  return (
    <div className="card2 p-6">
      <div className="flex items-center justify-between mb-3">
        <h3 className="t-h3">Active sessions</h3>
        {sessions.length > 1 && <button onClick={terminateOthers} disabled={busy === "others"} className="btn btn-secondary btn-sm" data-testid="terminate-others">{busy === "others" ? <Loader2 size={14} className="animate-spin" /> : <X size={14} />}Terminate all other sessions</button>}
      </div>
      {sessions.length === 0 ? (
        <p className="t-body-sm text-slate2">No active sessions found.</p>
      ) : (
        <div className="space-y-2">
          {sessions.map((s) => (
            <div key={s.id} className="flex items-center justify-between p-3 border border-border2 rounded-md" data-testid={`session-${s.id}`}>
              <div className="flex items-center gap-3 min-w-0">
                <Monitor size={18} className="text-slate2 flex-shrink-0" />
                <div className="min-w-0">
                  <div className="t-body-sm font-medium truncate">
                    {s.user_agent ? s.user_agent.slice(0, 50) : "Unknown device"}
                    {s.is_current && <span className="badge2 badge-verified ml-2">This session</span>}
                  </div>
                  <div className="t-caption">IP {s.ip_address || "—"} · Last active {s.last_active ? new Date(s.last_active).toLocaleString() : "—"}</div>
                </div>
              </div>
              {!s.is_current && <button onClick={() => terminate(s.id)} disabled={busy === s.id} className="btn btn-ghost btn-sm text-rose2" data-testid={`terminate-${s.id}`}>{busy === s.id ? <Loader2 size={14} className="animate-spin" /> : <X size={14} />}Terminate</button>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function SettingsPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const hashTab = (location.hash || "").replace("#", "");
  const initialTab = TABS.some((t) => t.id === hashTab) ? hashTab : "profile";
  const [tab, setTab] = useState(initialTab);
  const [firm, setFirm] = useState(null);

  // Keep tab in sync with URL hash for cross-section links (#security etc.)
  useEffect(() => {
    if (hashTab && TABS.some((t) => t.id === hashTab) && hashTab !== tab) {
      setTab(hashTab);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hashTab]);

  const onTabClick = (id) => {
    setTab(id);
    navigate(`#${id}`, { replace: true });
  };

  const refresh = async () => { try { const { data } = await api.get("/api/firm/me"); setFirm(data); } catch {} };
  useEffect(() => { refresh(); }, []);

  return (
    <AppShell>
      <PageHeader title="Settings" subtitle="Manage your firm profile, team, branding, and security." />
      <div className="flex flex-col lg:flex-row gap-6">
        <aside className="lg:w-56 flex-shrink-0">
          <div className="card2 p-2 lg:sticky lg:top-20">
            {TABS.map((t) => (
              <button key={t.id} onClick={() => onTabClick(t.id)} className={`w-full flex items-center gap-2 px-3 py-2.5 rounded-md t-body-sm font-medium transition-colors ${tab === t.id ? "bg-indigo2 text-white" : "text-slate2 hover:bg-surface hover:text-ink"}`} data-testid={`tab-${t.id}`}>
                <t.icon size={16} />{t.label}
              </button>
            ))}
          </div>
        </aside>
        <div className="flex-1 min-w-0">
          {!firm ? <Loader2 size={28} className="animate-spin text-indigo2 mx-auto" /> : (
            <>
              {tab === "profile" && <FirmProfileTab firm={firm} refresh={refresh} />}
              {tab === "branding" && <BrandingTab firm={firm} refresh={refresh} />}
              {tab === "team" && <TeamTab />}
              {tab === "notifications" && <NotificationsTab />}
              {tab === "security" && <SecurityTab />}
              {tab === "api" && <ApiTab />}
              {tab === "integrations" && <WebhooksTab />}
              {tab === "billing" && <BillingTab />}
              {tab === "data" && <DataTab firm={firm} refresh={refresh} />}
            </>
          )}
        </div>
      </div>
    </AppShell>
  );
}
